import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, ArrowLeft } from "lucide-react";
import { useUser } from "@clerk/clerk-react";
import { Link } from "wouter";
import { LectureDownloader } from '@/components/LectureDownloader';

export function CellularBasisHealthDisease() {
  const { isLoaded, user } = useUser();
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      title: "The Cellular Basis of Health and Disease",
      subtitle: "A Longevity Medicine Primer for Physicians",
      content: (
        <div className="text-center space-y-8">
          <div className="space-y-4">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              The Cellular Basis of Health and Disease
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Optimizing Healthspan through Cellular Understanding and Intervention
            </p>
          </div>
          
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-8 rounded-lg">
            <svg width="400" height="200" viewBox="0 0 400 200" xmlns="http://www.w3.org/2000/svg" className="mx-auto">
              <style>{`
                .cell-text { font-family: Arial, sans-serif; font-size: 12px; text-anchor: middle; }
                .title-text { font-size: 16px; font-weight: bold; }
                .cell { fill: #e6f3ff; stroke: #007bff; stroke-width: 2px; }
                .dna { fill: #ff6b6b; stroke: #cc0000; stroke-width: 1.5px; }
                .mitochondria { fill: #4ecdc4; stroke: #26a69a; stroke-width: 1.5px; }
              `}</style>
              
              <text x="200" y="25" className="title-text cell-text">Cellular Health → Systemic Health</text>
              
              <ellipse cx="200" cy="100" rx="120" ry="60" className="cell"/>
              <text x="200" y="70" className="cell-text" fontWeight="bold">Healthy Cell</text>
              
              <ellipse cx="150" cy="90" rx="15" ry="8" className="dna"/>
              <text x="150" y="95" className="cell-text" fontSize="8">DNA</text>
              
              <ellipse cx="220" cy="110" rx="12" ry="6" className="mitochondria"/>
              <ellipse cx="240" cy="100" rx="10" ry="5" className="mitochondria"/>
              <text x="230" y="125" className="cell-text" fontSize="8">Mitochondria</text>
              
              <text x="200" y="145" className="cell-text">Optimal Function</text>
              
              <path d="M 320 100 L 370 50" stroke="#28a745" strokeWidth="3" markerEnd="url(#greenArrow)"/>
              <path d="M 320 100 L 370 100" stroke="#28a745" strokeWidth="3" markerEnd="url(#greenArrow)"/>
              <path d="M 320 100 L 370 150" stroke="#28a745" strokeWidth="3" markerEnd="url(#greenArrow)"/>
              
              <text x="385" y="45" className="cell-text" fontSize="10">Tissue Health</text>
              <text x="385" y="105" className="cell-text" fontSize="10">Organ Function</text>
              <text x="385" y="155" className="cell-text" fontSize="10">Systemic Vitality</text>
              
              <text x="200" y="185" className="cell-text" fontWeight="bold" fill="#007bff">
                Longevity Medicine: Optimizing Cellular Function
              </text>
              
              <defs>
                <marker id="greenArrow" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
                  <polygon points="0 0, 10 3.5, 0 7" fill="#28a745"/>
                </marker>
              </defs>
            </svg>
          </div>
        </div>
      )
    },
    {
      title: "Shifting Focus: From Lifespan to Healthspan",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Traditional Medicine</h3>
              <p className="text-sm text-gray-700">
                Primarily focused on extending <em>lifespan</em>, often by treating established diseases.
              </p>
              
              <h3 className="text-lg font-semibold text-blue-600 mt-6">Longevity Medicine</h3>
              <p className="text-sm text-gray-700">
                A proactive approach to enhance <em>healthspan</em> – the years lived in good health, 
                vitality, and free from chronic disease and disability.
              </p>
              
              <div className="bg-blue-50 p-4 rounded-lg mt-4">
                <h4 className="font-semibold text-blue-900">Core Principle</h4>
                <p className="text-sm text-blue-800">
                  The vitality of an organism is inextricably linked to the health of its 
                  constituent cells. Optimal cellular function translates to robust tissue, 
                  organ, and systemic health.
                </p>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <svg width="450" height="180" viewBox="0 0 450 180" xmlns="http://www.w3.org/2000/svg">
                <style>{`
                  .text { font-family: Arial, sans-serif; }
                  .title-text { font-size: 14px; font-weight: bold; }
                  .bar-text { font-size: 12px; }
                  .axis-text { font-size: 10px; }
                `}</style>
                <text x="100" y="30" className="title-text" textAnchor="middle">Traditional Focus: Lifespan</text>
                <rect x="20" y="40" width="160" height="30" fill="#d3d3d3" stroke="#000"/>
                <rect x="20" y="40" width="120" height="30" fill="#8fbc8f"/>
                <rect x="140" y="40" width="40" height="30" fill="#ff6347"/>
                <text x="75" y="60" className="bar-text" fill="white">Healthy Years</text>
                <text x="158" y="60" className="bar-text" fill="white">Morbidity</text>
                <text x="100" y="85" className="axis-text">Total Years</text>

                <text x="350" y="30" className="title-text" textAnchor="middle">Longevity Focus: Healthspan</text>
                <rect x="270" y="40" width="160" height="30" fill="#d3d3d3" stroke="#000"/>
                <rect x="270" y="40" width="140" height="30" fill="#32cd32"/>
                <rect x="410" y="40" width="20" height="30" fill="#ff6347"/>
                <text x="335" y="60" className="bar-text" fill="white">Maximized Healthy Years</text>
                <text x="350" y="85" className="axis-text">Total Years (Goal: Compress Morbidity)</text>

                <line x1="100" y1="130" x2="350" y2="130" stroke="#007bff" strokeWidth="3" markerEnd="url(#arrowhead)"/>
                <text x="225" y="150" fontFamily="Arial" fontSize="14px" fill="#007bff" textAnchor="middle">Paradigm Shift: Enhancing Quality of Life</text>
                <defs>
                  <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#007bff"/>
                  </marker>
                </defs>
              </svg>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Functional Medicine: Addressing Root Cellular Causes",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <p className="text-sm text-gray-700">
                Aligns intrinsically with longevity goals by seeking to identify and address the 
                <em> root causes</em> of disease at cellular and systemic levels.
              </p>
              
              <p className="text-sm text-gray-700">
                Moves beyond symptom management to investigate the "why" behind physiological 
                imbalances (e.g., fatigue as mitochondrial dysfunction, gut dysbiosis, nutrient deficiencies).
              </p>
              
              <p className="text-sm text-gray-700">
                Emphasizes a personalized, patient-centered, systems-biology approach.
              </p>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-green-900">Clinical Relevance</h4>
                <p className="text-sm text-green-800">
                  This framework guides diagnostic inquiry and therapeutic selection to 
                  optimize cellular health proactively.
                </p>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <svg width="400" height="200" xmlns="http://www.w3.org/2000/svg">
                <style>{`
                  .text { font-family: Arial, sans-serif; font-size: 12px; text-anchor: middle; }
                  .title-text { font-size: 14px; font-weight: bold; }
                  .box { fill: #e6f3ff; stroke: #007bff; stroke-width: 1.5px; rx:5; ry:5;}
                  .arrow { stroke: #007bff; stroke-width: 2px; fill: none; marker-end: url(#fm-arrow); }
                `}</style>
                <defs>
                  <marker id="fm-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                    <path d="M 0 0 L 10 5 L 0 10 z" fill="#007bff" />
                  </marker>
                </defs>
                <text x="200" y="25" className="title-text">Functional Medicine Lens</text>
                <rect x="50" y="50" width="120" height="40" className="box"/>
                <text x="110" y="75" className="text">Symptom(s)</text>
                <text x="110" y="62" className="text">(e.g., Chronic Fatigue)</text>
                <path d="M 170 70 Q 200 70 230 100" className="arrow" />
                <rect x="200" y="90" width="180" height="90" className="box"/>
                <text x="290" y="110" className="text" fontWeight="bold">Investigate "Why?" - Root Causes:</text>
                <text x="290" y="130" className="text">- Mitochondrial Dysfunction</text>
                <text x="290" y="145" className="text">- Impaired Proteostasis</text>
                <text x="290" y="160" className="text">- Chronic Inflammation</text>
                <text x="290" y="175" className="text">- Nutrient Imbalances</text>
                <text x="200" y="195" className="text" fontWeight="bold">Target Cellular Mechanisms for Health Optimization</text>
              </svg>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Hallmarks of Aging: Cellular Drivers of Decline (Part 1)",
      content: (
        <div className="space-y-6">
          <p className="text-sm text-gray-700 mb-4">
            Aging is characterized by interconnected cellular changes. Addressing these is key to longevity.
          </p>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-red-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-red-900 mb-3">1. Mitochondrial Dysfunction & Bioenergetic Decline</h3>
              <div className="space-y-2 text-sm">
                <p><strong>Role:</strong> ATP production, ROS signaling, Ca²⁺ homeostasis.</p>
                <p><strong>Age-related Changes:</strong> ↓Biogenesis, impaired Electron Transport Chain (ETC), mtDNA mutations.</p>
                <p><strong>Consequences:</strong> Cellular energy deficit, ↑oxidative stress, tissue decline (sarcopenia, neurodegeneration).</p>
                <p><strong>Clinical:</strong> Fatigue, exercise intolerance. Test: Organic acids (lactate, pyruvate), CoQ10, carnitine.</p>
                <p><strong>Therapy:</strong> Exercise, CoQ10 (100-300mg), L-carnitine (1-2g), PGC-1α activators (Resveratrol), SS-31 (Elamipretide - investigational), NT Factor.</p>
                <p className="text-xs italic">Study: Elamipretide improved mitochondrial ATP production in older adults (Kara et al., 2021).</p>
              </div>
            </div>
            
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">2. Impaired Proteostasis & Autophagy Dysfunction</h3>
              <div className="space-y-2 text-sm">
                <p><strong>Role:</strong> Maintaining functional proteome (synthesis, folding, degradation via Ubiquitin-Proteasome System & Autophagy).</p>
                <p><strong>Age-related Changes:</strong> Decline in UPS and autophagy (macroautophagy, Chaperone-Mediated Autophagy - CMA).</p>
                <p><strong>Consequences:</strong> Accumulation of misfolded proteins (amyloid-β, tau), cellular toxicity.</p>
                <p><strong>Clinical:</strong> Neurodegenerative diseases.</p>
                <p><strong>Therapy:</strong> Caloric restriction (CR), exercise, spermidine (1-5mg), rapamycin (mTOR inhibitor - see later).</p>
                <p className="text-xs italic">Note: CMA decline linked to reduced LAMP2A (Cuervo & Dice, 2000s).</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Hallmarks of Aging (Part 2)",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-orange-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-orange-900 mb-3">3. Cellular Senescence & SASP</h3>
              <div className="space-y-2 text-sm">
                <p><strong>Role:</strong> Irreversible cell cycle arrest (p53/p21, p16^INK4a/pRB). Acutely beneficial (tumor suppression).</p>
                <p><strong>Age-related Changes:</strong> Chronic accumulation of senescent cells secreting pro-inflammatory Senescence-Associated Secretory Phenotype (SASP: IL-6, IL-8, MMPs).</p>
                <p><strong>Consequences:</strong> "Inflammaging," tissue disruption, fibrosis, fuels age-related diseases.</p>
                <p><strong>Clinical:</strong> Chronic inflammation, arthritis. Test: p16^INK4a (research), hs-CRP, IL-6.</p>
                <p><strong>Therapy:</strong> Senolytics (intermittent Dasatinib 100mg + Quercetin 1000mg for 2-3 days; Fisetin 20mg/kg/day for 2 days - investigational), Senomorphics.</p>
                <p className="text-xs italic">Study: D+Q pilot in IPF showed improved physical function (Hickson et al., 2019).</p>
              </div>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-purple-900 mb-3">4. Epigenetic Alterations & Genomic Instability</h3>
              <div className="space-y-2 text-sm">
                <p><strong>Role:</strong> Heritable gene function changes (DNA methylation, histone modifications).</p>
                <p><strong>Age-related Changes:</strong> Epigenetic drift (global hypomethylation, site-specific hypermethylation), altered histone marks.</p>
                <p><strong>Consequences:</strong> Inappropriate gene expression, genomic instability.</p>
                <p><strong>Clinical:</strong> Epigenetic clocks (Horvath, GrimAge) as biomarkers of biological age.</p>
                <p><strong>Therapy:</strong> Diet (folate, B12), lifestyle, SAMe, NAD⁺ precursors, HDAC inhibitors (e.g., butyrate from fiber). Metabolic-epigenetic crosstalk is key (acetyl-CoA, SAM, NAD⁺, α-KG).</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Hallmarks (Part 3) & Key Cellular States",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900 mb-2">5. Stem Cell Exhaustion</h3>
                <p className="text-sm">↓Number & function → impaired regeneration, frailty. Therapy: Senolytics (improve niche), GDF11 (controversial), peptides, Rapamycin.</p>
              </div>
              
              <div className="bg-red-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-red-900 mb-2">6. Altered Intercellular Communication (Inflammaging)</h3>
                <p className="text-sm">↑Pro-inflammatory signals (SASP, gut dysbiosis, DAMPs, NF-κB) → chronic low-grade inflammation. Therapy: Anti-inflammatory diet, lifestyle, senolytics, gut health.</p>
              </div>
            </div>
            
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">Metabolic Flexibility: The Key to Cellular Adaptation</h3>
              <div className="space-y-2 text-sm">
                <p><strong>Definition:</strong> Ability to efficiently switch fuel utilization (glucose vs. fatty acids) based on demand/availability.</p>
                <p><strong>Impairment:</strong> Common in aging, obesity, T2D; contributes to insulin resistance, hyperglycemia, hyperlipidemia.</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <svg width="550" height="160" xmlns="http://www.w3.org/2000/svg">
              <style>{`
                .header { font-family: Arial, sans-serif; font-size: 11px; font-weight: bold; fill: #333; }
                .cell { font-family: Arial, sans-serif; font-size: 10px; fill: #333; }
                .title { font-family: Arial, sans-serif; font-size: 12px; font-weight: bold; text-anchor: middle;}
              `}</style>
              <text x="275" y="15" className="title">Assessing & Improving Metabolic Flexibility</text>
              <rect x="10" y="25" width="530" height="20" fill="#f0f0f0"/>
              <text x="60" y="38" className="header">Parameter</text>
              <text x="190" y="38" className="header">Clinical Implication</text>
              <text x="350" y="38" className="header">Intervention Strategy</text>

              <text x="15" y="58" className="cell">Fasting Gluc/Ins, HOMA-IR</text>
              <text x="150" y="58" className="cell">Insulin resistance</text>
              <text x="280" y="58" className="cell">Low-glycemic diet, TRE/IF, Exercise, Metformin</text>

              <text x="15" y="78" className="cell">OGTT + Insulin</text>
              <text x="150" y="78" className="cell">Postprandial response</text>
              <text x="280" y="78" className="cell">Diet modification, Exercise</text>

              <text x="15" y="98" className="cell">RQ (Indirect Calorimetry)</text>
              <text x="150" y="98" className="cell">Substrate utilization</text>
              <text x="280" y="98" className="cell">Exercise (endurance), Keto, Fasting</text>

              <text x="15" y="118" className="cell">CGM Patterns</text>
              <text x="150" y="118" className="cell">Glycemic variability</text>
              <text x="280" y="118" className="cell">Personalized diet, Meal timing</text>

              <text x="15" y="138" className="cell">Serum Ketones (BHB)</text>
              <text x="150" y="138" className="cell">Shift to fat metabolism</text>
              <text x="280" y="138" className="cell">Keto diet, Fasting, MCT oil</text>
            </svg>
          </div>
          
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-yellow-900 mb-2">Redox Balance & Oxidative Stress</h3>
            <p className="text-sm">ROS: dual role (signaling "eustress" vs. damage "distress"). Antioxidant Defense (AOD) vs. ROS. Hormesis is key. Test: 8-OHdG, F2-isoprostanes, GSH/GSSG, oxLDL, HRV. Therapy: Bolster AOD (Nrf2), hormetic stressors (exercise, CR). Avoid high-dose non-specific antioxidants.</p>
          </div>
        </div>
      )
    },
    {
      title: "Key Signaling Pathways: Master Regulators",
      content: (
        <div className="space-y-6">
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Cells constantly sense and respond to their environment (nutrients, growth factors, stress) 
              via intricate signaling pathways.
            </p>
            <p className="text-sm text-gray-700">
              These pathways are master regulators of fundamental cellular processes: gene expression, 
              metabolism, growth, stress resistance, and ultimately, the pace of aging.
            </p>
            <p className="text-sm text-gray-700">
              Crucially, these pathways are highly interconnected, forming a complex regulatory web.
            </p>
            <p className="text-sm text-gray-700 font-semibold">
              Understanding and therapeutically modulating these pathways is central to longevity medicine.
            </p>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <svg width="450" height="250" xmlns="http://www.w3.org/2000/svg">
              <style>{`
                .text { font-family: Arial, sans-serif; font-size: 11px; text-anchor: middle; }
                .input { fill: #add8e6; stroke: #4682b4; }
                .hub { fill: #90ee90; stroke: #2e8b57; }
                .output { fill: #ffcccb; stroke: #dc143c; }
                .arrow { stroke: #555; stroke-width: 1.5px; fill: none; marker-end: url(#s-arrow); }
              `}</style>
              <defs>
                <marker id="s-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
                  <path d="M 0 0 L 10 5 L 0 10 z" fill="#555" />
                </marker>
              </defs>
              <text x="225" y="20" fontSize="14px" fontWeight="bold" className="text">Signaling Network & Cellular Outcomes</text>
              <ellipse cx="70" cy="60" rx="50" ry="20" className="input"/><text x="70" y="65" className="text">Nutrients</text>
              <ellipse cx="70" cy="110" rx="50" ry="20" className="input"/><text x="70" y="115" className="text">Growth Factors</text>
              <ellipse cx="70" cy="160" rx="50" ry="20" className="input"/><text x="70" y="165" className="text">Stressors</text>
              <rect x="150" y="70" width="150" height="90" className="hub" rx="10" ry="10"/>
              <text x="225" y="90" className="text" fontWeight="bold">Signaling Network Hub</text>
              <text x="225" y="105" className="text">(IIS, mTOR, AMPK, Sirtuins,</text>
              <text x="225" y="120" className="text">NF-κB, Nrf2, JAK/STAT, TGF-β)</text>
              <text x="225" y="135" className="text">Interconnected Pathways</text>
              <path d="M 120 60 Q 135 70 150 80" className="arrow"/>
              <path d="M 120 110 L 150 115" className="arrow"/>
              <path d="M 120 160 Q 135 150 150 145" className="arrow"/>
              <rect x="330" y="40" width="110" height="30" className="output" rx="5"/><text x="385" y="60" className="text">Autophagy</text>
              <rect x="330" y="80" width="110" height="30" className="output" rx="5"/><text x="385" y="100" className="text">Protein Synthesis</text>
              <rect x="330" y="120" width="110" height="30" className="output" rx="5"/><text x="385" y="140" className="text">Inflammation</text>
              <rect x="330" y="160" width="110" height="30" className="output" rx="5"/><text x="385" y="180" className="text">Stress Resistance</text>
              <rect x="330" y="200" width="110" height="30" className="output" rx="5"/><text x="385" y="220" className="text">Cell Fate</text>
              <path d="M 300 90 Q 315 70 330 55" className="arrow"/>
              <path d="M 300 105 L 330 95" className="arrow"/>
              <path d="M 300 120 L 330 135" className="arrow"/>
              <path d="M 300 135 Q 315 160 330 175" className="arrow"/>
              <path d="M 300 145 Q 315 190 330 215" className="arrow"/>
              <text x="225" y="245" className="text" fontWeight="bold" fill="#0056b3">Impacts Healthspan & Longevity</text>
            </svg>
          </div>
        </div>
      )
    },
    {
      title: "Insulin/IGF-1 Signaling (IIS): Growth, Metabolism, Aging",
      content: (
        <div className="space-y-6">
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              <strong>Mechanism:</strong> Insulin/IGF-1 → IR/IGF-1R → IRS → PI3K/Akt → ↓FOXO activity. Promotes growth, nutrient storage.
            </p>
            <p className="text-sm text-gray-700">
              <strong>Role in Aging:</strong> Reduced IIS extends lifespan in models (via FOXO: stress resistance, DNA repair). Human: moderate reduction beneficial.
            </p>
            <p className="text-sm text-gray-700">
              <strong>Dysregulation:</strong> Insulin resistance, hyperinsulinemia → Metabolic syndrome, T2D, CVD, neurodegeneration, cancer risk.
            </p>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <svg width="600" height="200" xmlns="http://www.w3.org/2000/svg">
              <style>{`
                .header { font-family: Arial, sans-serif; font-size: 11px; font-weight: bold; fill: #333; }
                .cell { font-family: Arial, sans-serif; font-size: 10px; fill: #333; }
                .title { font-family: Arial, sans-serif; font-size: 12px; font-weight: bold; text-anchor: middle;}
              `}</style>
              <text x="300" y="15" className="title">IIS Pathway: Key Biomarkers & Interventions</text>
              <rect x="10" y="25" width="580" height="20" fill="#f0f0f0"/>
              <text x="70" y="38" className="header">Biomarker</text>
              <text x="200" y="38" className="header">Optimal Range (General)</text>
              <text x="380" y="38" className="header">Key Interventions (Dosing Examples)</text>

              <text x="15" y="58" className="cell">Fasting Glucose</text><text x="160" y="58" className="cell">&lt; 95 mg/dL</text><text x="300" y="58" className="cell">Diet (Low GI/GL, TRE/IF), Exercise</text>
              <text x="15" y="73" className="cell">Fasting Insulin</text><text x="160" y="73" className="cell">&lt; 8 µU/mL (ideal &lt;5)</text><text x="300" y="73" className="cell">Metformin (500mg QD to 1000mg BID XR)</text>
              <text x="15" y="88" className="cell">HOMA-IR</text><text x="160" y="88" className="cell">&lt; 1.5 (ideal &lt;1.0)</text><text x="300" y="88" className="cell">Monitor: eGFR, B12 (Metformin)</text>
              <text x="15" y="103" className="cell">HbA1c</text><text x="160" y="103" className="cell">&lt; 5.4%</text><text x="300" y="103" className="cell">Acarbose (25-50mg TID with meals)</text>
              <text x="15" y="118" className="cell">IGF-1</text><text x="160" y="118" className="cell">Age-specific mid-range</text><text x="300" y="118" className="cell">SGLT2i (e.g., Cana 100mg QD). Monitor renal, GMI.</text>
              <text x="15" y="133" className="cell">TG/HDL Ratio</text><text x="160" y="133" className="cell">&lt; 1.5 (ideal &lt;1.0)</text><text x="300" y="133" className="cell">Berberine (500mg BID-TID), ALA, Mg</text>
              <text x="15" y="148" className="cell">Adiponectin</text><text x="160" y="148" className="cell">Higher is better</text><text x="300" y="148" className="cell">Weight management, Exercise</text>
              <text x="15" y="163" className="cell" fontStyle="italic">FOXO3A SNPs</text><text x="160" y="163" className="cell" fontStyle="italic">Longevity-associated</text><text x="300" y="163" className="cell" fontStyle="italic">Informs predisposition</text>
            </svg>
          </div>
          
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h4 className="font-semibold text-yellow-900">Critique</h4>
            <p className="text-sm text-yellow-800">
              Aim for moderated IIS activity, not complete ablation. Laron syndrome (GH-R def.) protects 
              vs cancer/T2D but not extreme longevity.
            </p>
          </div>
        </div>
      )
    },
    {
      title: "mTOR Pathway: Nutrient Sensing, Growth, and Aging",
      content: (
        <div className="space-y-6">
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              <strong>Mechanism:</strong> mTORC1 activated by nutrients (amino acids), growth factors (via IIS/Akt), 
              high energy. Promotes protein/lipid/nucleotide synthesis; inhibits autophagy & lysosomal biogenesis.
            </p>
            <p className="text-sm text-gray-700">
              <strong>Role in Aging:</strong> Chronic mTORC1 hyperactivation → ↓autophagy, ↑senescence, 
              stem cell depletion. Inhibition (Rapamycin, CR) extends lifespan/healthspan in models.
            </p>
            <p className="text-sm text-gray-700">
              <strong>Dysregulation:</strong> Cancer, metabolic diseases, neurodegeneration, CVD.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">Biomarkers</h3>
              <div className="space-y-2 text-sm">
                <p><strong>Research/Specialty:</strong> p-S6 Ribosomal Protein (p-S6RP), p-4E-BP1.</p>
                <p><strong>Indirect:</strong> autophagy markers (LC3-II/I, p62 - interpret with flux), amino acid levels (BCAAs).</p>
              </div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-green-900 mb-3">Therapies</h3>
              <div className="space-y-2 text-sm">
                <p><strong>Diet:</strong> CR, Protein Restriction (esp. leucine), TRE/IF.</p>
                <p><strong>Nutraceuticals (indirect):</strong> Metformin, Berberine (via AMPK), Resveratrol.</p>
              </div>
            </div>
          </div>
          
          <div className="bg-red-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-red-900 mb-3">Rapamycin (Sirolimus) & Rapalogs (Highly Investigational for Longevity)</h3>
            <div className="space-y-2 text-sm">
              <p><strong>Mechanism:</strong> Allosteric mTORC1 inhibitor.</p>
              <p><strong>Dosing:</strong> Intermittent (e.g., 3-7mg once weekly) or low daily (0.5-2mg).</p>
              <p><strong>Monitoring:</strong> CBC, CMP, lipids, glucose/HbA1c.</p>
              <p><strong>Side Effects:</strong> Stomatitis, dyslipidemia, glucose intolerance, impaired wound healing, immunosuppression.</p>
              <p className="text-xs italic">
                Critique: Potent effects, but side effects necessitate cautious, intermittent dosing to balance 
                benefits (autophagy, stem cell rescue, immunosenescence reversal) vs. risks.
              </p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "AMPK Pathway: The Cellular Energy Sensor",
      content: (
        <div className="space-y-6">
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              <strong>Mechanism:</strong> Activated by low energy (↑AMP/ATP ratio), exercise, CR. Stimulates ATP production 
              (glucose uptake, FA oxidation, mitochondrial biogenesis, autophagy); inhibits ATP consumption (protein/lipid synthesis, mTORC1).
            </p>
            <p className="text-sm text-gray-700">
              <strong>Role in Aging:</strong> Activity declines with age. Activation (CR, Metformin) extends lifespan/healthspan. 
              Improves metabolic health, stress resistance, autophagy.
            </p>
            <p className="text-sm text-gray-700">
              <strong>Dysregulation:</strong> Metabolic syndrome, T2D, CVD, cancer, neurodegeneration.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-purple-900 mb-3">Biomarkers</h3>
                <div className="space-y-2 text-sm">
                  <p><strong>Research/Specialty:</strong> p-AMPKα (Thr172), p-ACC (Ser79).</p>
                  <p><strong>Indirect:</strong> improved glucose, lipids, HOMA-IR. Adiponectin levels.</p>
                </div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900 mb-3">Therapies</h3>
                <div className="space-y-2 text-sm">
                  <p><strong>Lifestyle:</strong> Exercise (potent activator), CR/Fasting.</p>
                  <p><strong>Pharma:</strong></p>
                  <ul className="ml-4 space-y-1">
                    <li>• Metformin (500mg QD to 1000mg BID XR): Primary AMPK activator (via mito complex I inhibition).</li>
                    <li>• Berberine (500mg BID-TID with meals): Natural AMPK activator.</li>
                  </ul>
                  <p><strong>Nutraceuticals:</strong> Resveratrol, Quercetin, Curcumin, EGCG, ALA, Gynostemma.</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <svg width="350" height="150" xmlns="http://www.w3.org/2000/svg">
                <style>{`.text { font-family: Arial, sans-serif; font-size: 12px; text-anchor: middle; }`}</style>
                <line x1="50" y1="75" x2="300" y2="75" stroke="black" strokeWidth="3"/>
                <polygon points="175,50 165,75 185,75" fill="black"/>
                <rect x="30" y="45" width="80" height="30" fill="#ffcccb" rx="5"/> <text x="70" y="65" className="text">ATP High</text>
                <rect x="240" y="45" width="80" height="30" fill="#add8e6" rx="5"/> <text x="280" y="65" className="text">AMP High</text>
                <text x="175" y="100" className="text" fontWeight="bold">AMPK Activity</text>
                <text x="70" y="120" className="text" fill="red">↓ OFF</text>
                <text x="70" y="135" className="text" fill="red">(Anabolism Favored)</text>
                <text x="280" y="120" className="text" fill="green">↑ ON</text>
                <text x="280" y="135" className="text" fill="green">(Catabolism Favored)</text>
              </svg>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Sirtuins & NAD⁺: Guardians of Cellular Health & Longevity",
      content: (
        <div className="space-y-6">
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              <strong>Mechanism:</strong> Sirtuins (SIRT1-7) are NAD⁺-dependent deacetylases/deacylases, 
              linking metabolic state (NAD⁺ availability) to gene regulation.
            </p>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="bg-blue-50 p-3 rounded">
                <h4 className="font-semibold text-blue-900">SIRT1 (nucleus/cyto)</h4>
                <p className="text-xs">Stress resistance, metabolism, inflammation (via p53, NF-κB, FOXO, PGC-1α).</p>
              </div>
              <div className="bg-green-50 p-3 rounded">
                <h4 className="font-semibold text-green-900">SIRT3 (mito)</h4>
                <p className="text-xs">Mitochondrial metabolism, antioxidant defense (MnSOD, IDH2).</p>
              </div>
              <div className="bg-purple-50 p-3 rounded">
                <h4 className="font-semibold text-purple-900">SIRT6 (nucleus)</h4>
                <p className="text-xs">DNA repair, genomic stability, glucose metabolism.</p>
              </div>
            </div>
            
            <p className="text-sm text-gray-700">
              <strong>Role in Aging:</strong> NAD⁺ levels decline with age (↓synthesis via NAMPT, ↑consumption by PARPs, CD38) 
              → ↓sirtuin activity. Restoring NAD⁺/activating sirtuins extends lifespan/healthspan in models.
            </p>
            <p className="text-sm text-gray-700">
              <strong>Dysregulation:</strong> Metabolic syndrome, T2D, CVD, neurodegeneration.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-yellow-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-yellow-900 mb-3">Biomarkers</h3>
              <div className="space-y-2 text-sm">
                <p>NAD⁺/NADH levels (whole blood/PBMCs - specialty labs), urinary NAD⁺ metabolites (MeNAM).</p>
                <p><strong>Indirect:</strong> improved metabolic/inflammatory markers.</p>
              </div>
            </div>
            
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">Therapies</h3>
              <div className="space-y-2 text-sm">
                <p><strong>NAD⁺ Precursors:</strong></p>
                <ul className="ml-4 space-y-1">
                  <li>• NMN (250-1000mg/day, oral/sublingual)</li>
                  <li>• NR (250-1000mg/day, oral)</li>
                </ul>
                <p><strong>Sirtuin Activators (STACs):</strong> Resveratrol (150-500mg micronized trans-resveratrol), Pterostilbene (50-150mg).</p>
                <p><strong>Lifestyle:</strong> CR/Fasting, Exercise (boost NAD⁺).</p>
                <p><strong>CD38 Inhibitors (Research):</strong> Apigenin (50-100mg), Quercetin.</p>
                <p className="text-xs italic">
                  Study: NMN improved muscle insulin sensitivity in prediabetic women (Yoshino et al., 2021).
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "NF-κB Pathway: The Inflammation Switch & Driver of \"Inflammaging\"",
      content: (
        <div className="space-y-6">
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              <strong>Mechanism:</strong> Activated by pro-inflammatory cytokines (TNF-α, IL-1β), PAMPs/DAMPs, ROS. 
              IKK phosphorylates IκB → NF-κB (p50/p65) nuclear translocation → transcription of inflammatory genes 
              (cytokines, chemokines, COX-2, SASP components).
            </p>
            <p className="text-sm text-gray-700">
              <strong>Role in Aging:</strong> Activity chronically increases with age, driving "inflammaging." 
              Contributes to cellular senescence and most age-related diseases. Inhibition delays age-related symptoms in models.
            </p>
            <p className="text-sm text-gray-700">
              <strong>Dysregulation:</strong> CVD, neurodegeneration, arthritis, T2D, cancer.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-red-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-red-900 mb-3">Biomarkers</h3>
              <div className="space-y-2 text-sm">
                <p>hs-CRP (&lt;1.0 mg/L, ideally &lt;0.5), IL-6, TNF-α, ESR, Neutrophil-to-Lymphocyte Ratio (NLR).</p>
              </div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-green-900 mb-3">Therapies</h3>
              <div className="space-y-2 text-sm">
                <p><strong>Diet/Lifestyle:</strong> Anti-inflammatory diet (Mediterranean), regular exercise, stress management, gut health, adequate sleep.</p>
                <p><strong>Nutraceuticals:</strong></p>
                <ul className="ml-4 space-y-1">
                  <li>• Curcumin (500-1000mg bioavailable form BID-TID): Potent NF-κB inhibitor</li>
                  <li>• Omega-3s (EPA/DHA 1-3g/day): Precursors to anti-inflammatory resolvins</li>
                  <li>• Boswellia, Ginger, Resveratrol</li>
                  <li>• Vitamin D (target 40-60 ng/mL), Magnesium, Zinc</li>
                </ul>
                <p><strong>Pharma:</strong> Low-dose Aspirin (81mg QD, if indicated for CVD prevention). 
                NSAIDs/Corticosteroids (not for chronic longevity use).</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Nrf2 Pathway: Master of Antioxidant & Detox Defense",
      content: (
        <div className="space-y-6">
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              <strong>Mechanism:</strong> Under stress (ROS, electrophiles), Nrf2 dissociates from Keap1 → 
              nuclear translocation → binds Antioxidant Response Elements (AREs) → transcription of cytoprotective genes.
            </p>
            
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-semibold text-blue-900">Target Genes</h4>
              <p className="text-sm">Antioxidant enzymes (SOD, Catalase, GPx, HMOX1), GSH synthesis (GCLC, GCLM), 
              Detoxification (NQO1, GSTs), Proteostasis components.</p>
            </div>
            
            <p className="text-sm text-gray-700">
              <strong>Role in Aging:</strong> Activity declines with age → ↓antioxidant capacity, ↑oxidative damage. 
              Activation enhances stress resistance, reduces age-related pathology. Embodies hormetic principle.
            </p>
            <p className="text-sm text-gray-700">
              <strong>Dysregulation:</strong> CVD, neurodegeneration, cancer (complex: protective early, can promote resistance later), 
              lung/kidney/liver diseases.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-purple-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-purple-900 mb-3">Biomarkers</h3>
              <div className="space-y-2 text-sm">
                <p><strong>Research/Specialty:</strong> Expression of Nrf2 target genes (HMOX1, NQO1, GCLC in PBMCs). 
                GSH levels, oxidative stress markers (8-OHdG, F2-isoprostanes).</p>
              </div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-green-900 mb-3">Therapies (Nrf2 Activators)</h3>
              <div className="space-y-2 text-sm">
                <p><strong>Dietary:</strong> Sulforaphane (broccoli sprouts), Curcumin, Resveratrol, EGCG (green tea), 
                Garlic (allicin), Rosemary (rosmarinic acid).</p>
                <p><strong>Lifestyle:</strong> Exercise (can induce Nrf2).</p>
                <p><strong>Pharma:</strong> Bardoxolone methyl (investigational for CKD, complex safety profile). 
                Dimethyl fumarate (for MS).</p>
              </div>
            </div>
          </div>
          
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h4 className="font-semibold text-yellow-900">Critique</h4>
            <p className="text-sm text-yellow-800">
              Balanced, physiological Nrf2 modulation is key. Avoid chronic, excessive hyperactivation.
            </p>
          </div>
        </div>
      )
    },
    {
      title: "Other Key Pathways: JAK/STAT & TGF-β",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">JAK/STAT Pathway</h3>
              <div className="space-y-2 text-sm">
                <p><strong>Mechanism:</strong> Cytokines (e.g., IL-6, IFNs)/growth factors → JAKs activate → 
                STATs phosphorylate, dimerize, translocate → gene expression (immunity, inflammation, growth, SASP).</p>
                <p><strong>Aging/Disease:</strong> ↑Activity with age (e.g., via IL-6) → inflammaging, SASP, immunosenescence. 
                Dysregulated in autoimmune diseases, cancers.</p>
                <p><strong>Therapies:</strong> JAK inhibitors (Tofacitinib, Ruxolitinib) for specific diseases. 
                Potential senomorphic by reducing SASP effects. Significant side effects (infections, MACE, malignancy risk) 
                limit general longevity use.</p>
              </div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-green-900 mb-3">TGF-β Pathway</h3>
              <div className="space-y-2 text-sm">
                <p><strong>Mechanism:</strong> TGF-β → TGF-β receptors → SMAD signaling → gene expression 
                (cell growth, differentiation, apoptosis, immune regulation, ECM production).</p>
                <p><strong>Aging/Disease:</strong> Dysregulated activity contributes to fibrosis, immune dysfunction, 
                cancer progression. Both tumor suppressive and oncogenic roles depending on context.</p>
                <p><strong>Therapies:</strong> Complex. Targeting specific components under investigation 
                (e.g., TGF-β antagonists for fibrosis). Requires careful consideration of context and timing.</p>
              </div>
            </div>
          </div>
          
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h4 className="font-semibold text-yellow-900">Key Takeaway</h4>
            <p className="text-sm text-yellow-800">
              Important pathways, but direct interventions for longevity are currently limited by complexity 
              and side effect profiles. Focus on upstream modulators (e.g., reducing inflammation that drives JAK/STAT).
            </p>
          </div>
        </div>
      )
    },
    {
      title: "Clinical Application: Testing & Foundational Interventions",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">Comprehensive Baseline Assessment</h3>
              <div className="space-y-2 text-sm">
                <p><strong>Standard:</strong> CBC, CMP, Lipids, HbA1c, hs-CRP, Vitamin D (25-OH).</p>
                <p><strong>Hormonal:</strong> Thyroid (TSH, fT4, fT3, Ab), Sex hormones (age/sex appropriate).</p>
                <p><strong>Inflammation/Oxidative Stress:</strong> IL-6, TNF-α (if hs-CRP high), F2-isoprostanes, 8-OHdG, GSH/GSSG, oxLDL.</p>
                <p><strong>Nutrient Sensing:</strong> Fasting Insulin (for HOMA-IR), IGF-1, Adiponectin.</p>
                <p><strong>Mitochondrial:</strong> Urine organic acids, CoQ10, Carnitine.</p>
                <p><strong>Genetic (Optional):</strong> ApoE, MTHFR.</p>
                <p><strong>Epigenetic Clocks (Research/Specialty):</strong> DunedinPACE, GrimAge.</p>
              </div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-green-900 mb-3">Foundational Interventions (The "Non-Negotiables")</h3>
              <div className="space-y-2 text-sm">
                <p><strong>Diet:</strong> Whole-food, nutrient-dense, predominantly plant-rich, low glycemic load/index. 
                Consider TRE (e.g., 14-16h fast). Personalize.</p>
                <p><strong>Exercise:</strong> Mix of aerobic (150+ min/wk moderate or 75+ min/wk vigorous), 
                resistance (2-3x/wk), flexibility/balance.</p>
                <p><strong>Sleep:</strong> Prioritize 7-9 hours of quality sleep.</p>
                <p><strong>Stress Management:</strong> Mindfulness, meditation, yoga, nature.</p>
                <p><strong>Toxin Avoidance:</strong> Clean air, water, food; minimize endocrine-disrupting chemicals (EDCs).</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">Intervention Hierarchy</h3>
            <svg width="400" height="200" viewBox="0 0 400 200" xmlns="http://www.w3.org/2000/svg" className="mx-auto">
              <style>{`
                .pyramid-text { font-family: Arial, sans-serif; font-size: 12px; text-anchor: middle; font-weight: bold; }
                .level1 { fill: #4CAF50; stroke: #388E3C; stroke-width: 2; }
                .level2 { fill: #FFC107; stroke: #F57F17; stroke-width: 2; }
                .level3 { fill: #FF5722; stroke: #D84315; stroke-width: 2; }
              `}</style>
              
              <polygon points="50,180 350,180 250,60 150,60" className="level1"/>
              <text x="200" y="140" className="pyramid-text" fill="white">Lifestyle Foundation</text>
              <text x="200" y="155" className="pyramid-text" fill="white" fontSize="10">Diet • Exercise • Sleep • Stress Management</text>
              
              <polygon points="100,120 300,120 250,80 150,80" className="level2"/>
              <text x="200" y="105" className="pyramid-text" fill="white">Targeted Nutraceuticals</text>
              
              <polygon points="150,80 250,80 220,40 180,40" className="level3"/>
              <text x="200" y="65" className="pyramid-text" fill="white" fontSize="10">Pharmacological</text>
              <text x="200" y="75" className="pyramid-text" fill="white" fontSize="10">(if needed)</text>
            </svg>
          </div>
        </div>
      )
    },
    {
      title: "Clinical Application: Targeted Therapies & Monitoring",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">Targeted Nutraceuticals (Based on Biomarkers & Goals)</h3>
              <div className="space-y-2 text-sm">
                <p><strong>Insulin Sensitivity/AMPK:</strong> Berberine (500mg BID-TID), ALA (300-600mg R-ALA), Magnesium (200-400mg elemental).</p>
                <p><strong>NAD⁺/Sirtuins:</strong> NMN/NR (250-1000mg), Resveratrol/Pterostilbene.</p>
                <p><strong>Inflammation/NF-κB:</strong> Curcumin (bioavailable, 500mg BID), Omega-3s (1-2g EPA+DHA).</p>
                <p><strong>Nrf2 Activation:</strong> Sulforaphane (extract or sprouts), Rosmarinic acid.</p>
                <p><strong>Mitochondrial Support:</strong> CoQ10 (100-300mg ubiquinol), PQQ (10-20mg), L-Carnitine (1-2g).</p>
                <p><strong>Senolytics (Cautious, Intermittent, Investigational):</strong> Quercetin (500-1000mg) + Dasatinib (50-100mg) for 2-3 days/month; Fisetin (20mg/kg/day) for 2 days/month.</p>
              </div>
            </div>
            
            <div className="bg-red-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-red-900 mb-3">Pharmacological Interventions</h3>
              <div className="space-y-2 text-sm">
                <p className="text-xs italic">(Consider Risk/Benefit, Shared Decision Making)</p>
                <p><strong>Metformin:</strong> For metabolic dysfunction, pre-diabetes (off-label for longevity).</p>
                <p><strong>SGLT2 Inhibitors / Acarbose:</strong> For specific glycemic profiles.</p>
                <p><strong>Rapamycin:</strong> Highly experimental for longevity, intermittent dosing, close monitoring.</p>
              </div>
            </div>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-green-900 mb-3">Monitoring & Follow-Up</h3>
            <div className="space-y-2 text-sm">
              <p>Regularly reassess biomarkers (q3-6 months initially, then q6-12 months).</p>
              <p>Track subjective improvements (energy, cognition, pain, function).</p>
              <p>Adjust interventions based on response, tolerability, and evolving evidence.</p>
              <p><strong>Iterative Process:</strong> Test → Intervene → Monitor → Adjust.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Evidence Snapshot: Key Studies & Findings",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">Metformin (TAME Trial)</h3>
              <div className="space-y-2 text-sm">
                <p><strong>Design:</strong> Ongoing, large-scale trial to assess metformin's effect on delaying age-related diseases in non-diabetics.</p>
                <p><strong>Rationale:</strong> Preclinical data (AMPK activation, IIS modulation) & observational studies.</p>
                <p><strong>Critique:</strong> Awaiting results; potential for B12 deficiency, GI side effects.</p>
              </div>
            </div>
            
            <div className="bg-red-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-red-900 mb-3">Rapamycin (NIA-ITP; Mannick et al. 2014)</h3>
              <div className="space-y-2 text-sm">
                <p><strong>Findings (ITP):</strong> Consistently extends lifespan in mice, even late in life. 
                (Mannick): mTOR inhibitor (Everolimus) improved vaccine response in elderly humans.</p>
                <p><strong>Critique:</strong> Dose/schedule critical. Side effects at transplant doses significant. 
                Longevity dosing aims for transient mTORC1 inhibition, requires careful monitoring.</p>
              </div>
            </div>
            
            <div className="bg-orange-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-orange-900 mb-3">Senolytics (Hickson et al. 2019)</h3>
              <div className="space-y-2 text-sm">
                <p><strong>Findings:</strong> Pilot in Idiopathic Pulmonary Fibrosis (IPF) showed D+Q improved physical function 
                (6MWT, chair rise), reduced SASP markers.</p>
                <p><strong>Critique:</strong> Small study, specific disease. Need larger trials for broader aging. 
                Intermittent dosing seems key.</p>
              </div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-green-900 mb-3">NAD⁺ Precursors (Yoshino et al. 2021)</h3>
              <div className="space-y-2 text-sm">
                <p><strong>Findings:</strong> Generally show NMN/NR increase NAD⁺ levels. Some studies show modest 
                physiological benefits (e.g., NMN & muscle insulin sensitivity in prediabetic women).</p>
                <p><strong>Critique:</strong> Many studies small, short-term. Optimal dose, form, long-term effects, 
                and consistent clinical benefits still under investigation. Heterogeneity in results.</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Conclusion: Optimizing Cellular Health for Longevity",
      content: (
        <div className="space-y-6">
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Longevity medicine aims to enhance healthspan by targeting fundamental cellular aging processes 
              and their associated signaling pathways.
            </p>
            <p className="text-sm text-gray-700">
              A functional medicine approach, focusing on root causes, guides personalized assessment and intervention.
            </p>
            <p className="text-sm text-gray-700">
              Foundational lifestyle changes are paramount, complemented by targeted nutraceuticals and, 
              where appropriate, pharmacological agents.
            </p>
            <p className="text-sm text-gray-700">
              Many interventions are promising but require careful clinical judgment, ongoing risk/benefit analysis, 
              shared decision-making, and continued research.
            </p>
          </div>
          
          <div className="bg-blue-50 p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-blue-900 mb-4">Future Directions</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ul className="space-y-2 text-sm">
                <li>• Refinement of biomarker panels for biological age and pathway-specific activity</li>
                <li>• Development of safer, more targeted senolytics, NAD⁺ modulators, and pathway-specific drugs</li>
              </ul>
              <ul className="space-y-2 text-sm">
                <li>• Personalized "longevity stacks" based on 'omics and dynamic biomarker data</li>
                <li>• Greater integration of AI for predictive modeling and intervention selection</li>
              </ul>
            </div>
          </div>
          
          <div className="text-center mt-8">
            <svg width="300" height="150" viewBox="0 0 300 150" xmlns="http://www.w3.org/2000/svg" className="mx-auto">
              <style>{`
                .network-node { fill: #007bff; stroke: #0056b3; stroke-width: 2; }
                .network-connection { stroke: #28a745; stroke-width: 2; opacity: 0.6; }
                .center-node { fill: #28a745; stroke: #1e7e34; stroke-width: 3; }
              `}</style>
              
              <circle cx="150" cy="75" r="25" className="center-node"/>
              <text x="150" y="80" textAnchor="middle" fontSize="12" fontWeight="bold" fill="white">Cellular Health</text>
              
              <circle cx="75" cy="40" r="15" className="network-node"/>
              <text x="75" y="25" textAnchor="middle" fontSize="10">Mitochondria</text>
              
              <circle cx="225" cy="40" r="15" className="network-node"/>
              <text x="225" y="25" textAnchor="middle" fontSize="10">Autophagy</text>
              
              <circle cx="75" cy="110" r="15" className="network-node"/>
              <text x="75" y="125" textAnchor="middle" fontSize="10">Inflammation</text>
              
              <circle cx="225" cy="110" r="15" className="network-node"/>
              <text x="225" y="125" textAnchor="middle" fontSize="10">Senescence</text>
              
              <line x1="90" y1="50" x2="135" y2="65" className="network-connection"/>
              <line x1="210" y1="50" x2="165" y2="65" className="network-connection"/>
              <line x1="90" y1="100" x2="135" y2="85" className="network-connection"/>
              <line x1="210" y1="100" x2="165" y2="85" className="network-connection"/>
            </svg>
          </div>
        </div>
      )
    },
    {
      title: "SHIFT Framework for Longevity Medicine",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg text-center">
            <h3 className="text-lg font-medium text-blue-800 mb-2">A signaling-based model for diagnosing, interpreting, and intervening in aging-related decline</h3>
            <p className="text-sm text-gray-700">Structured around five core biological functions</p>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center text-white font-bold mr-3">S</div>
                <h3 className="text-lg font-medium text-amber-800">Sensing</h3>
              </div>
              <p className="text-sm text-gray-700 mb-2"><strong>Definition:</strong> The body's ability to perceive internal and external cues (e.g., nutrients, stress, light, redox status).</p>
              <p className="text-sm text-gray-700 mb-2"><strong>Pathways:</strong> mTOR, AMPK, insulin/IGF-1, sirtuins.</p>
              <p className="text-sm text-gray-700 mb-2"><strong>Example Biomarkers:</strong> Fasting insulin, HOMA-IR, leptin, glucose variability.</p>
              <p className="text-sm text-gray-700"><strong>Clinical Application:</strong> GLP-1 RAs, intermittent fasting, berberine, myo-inositol, CGMs.</p>
            </div>

            <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold mr-3">H</div>
                <h3 className="text-lg font-medium text-green-800">Homeostatic Defense</h3>
              </div>
              <p className="text-sm text-gray-700 mb-2"><strong>Definition:</strong> The maintenance of equilibrium in systems like blood pressure, metabolism, and electrolytes under stress.</p>
              <p className="text-sm text-gray-700 mb-2"><strong>Pathways:</strong> Autophagy, Nrf2, unfolded protein response.</p>
              <p className="text-sm text-gray-700 mb-2"><strong>Example Biomarkers:</strong> hs-CRP, homocysteine, GGT, ferritin.</p>
              <p className="text-sm text-gray-700"><strong>Clinical Application:</strong> NAC, sauna, cold exposure, hormetic stressors.</p>
            </div>

            <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white font-bold mr-3">I</div>
                <h3 className="text-lg font-medium text-red-800">Inflammation</h3>
              </div>
              <p className="text-sm text-gray-700 mb-2"><strong>Definition:</strong> Chronic low-grade inflammation that drives aging, immune dysregulation, and tissue dysfunction.</p>
              <p className="text-sm text-gray-700 mb-2"><strong>Pathways:</strong> NF-κB, inflammasome, COX/LOX, PGC-1α.</p>
              <p className="text-sm text-gray-700 mb-2"><strong>Example Biomarkers:</strong> hs-CRP, IL-6, TNF-α, omega-6/omega-3 ratio.</p>
              <p className="text-sm text-gray-700"><strong>Clinical Application:</strong> Omega-3s, curcumin, HRT, lifestyle medicine, targeted immunomodulation.</p>
            </div>

            <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold mr-3">F</div>
                <h3 className="text-lg font-medium text-purple-800">Fibrosis & Tissue Remodeling</h3>
              </div>
              <p className="text-sm text-gray-700 mb-2"><strong>Definition:</strong> The degradation or stiffening of tissue architecture via scarring, ECM breakdown, and cellular senescence.</p>
              <p className="text-sm text-gray-700 mb-2"><strong>Pathways:</strong> TGF-β, YAP/TAZ, MMPs.</p>
              <p className="text-sm text-gray-700 mb-2"><strong>Example Biomarkers:</strong> Fibroscan, elastography, GDF-15, collagen markers.</p>
              <p className="text-sm text-gray-700"><strong>Clinical Application:</strong> Rapamycin, senolytics, exosome therapy, regenerative peptides.</p>
            </div>

            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold mr-3">T</div>
                <h3 className="text-lg font-medium text-blue-800">Timekeeping & Biorhythms</h3>
              </div>
              <p className="text-sm text-gray-700 mb-2"><strong>Definition:</strong> The cellular machinery responsible for circadian rhythm, repair, and regenerative capacity.</p>
              <p className="text-sm text-gray-700 mb-2"><strong>Pathways:</strong> CLOCK genes, melatonin signaling, telomerase, NAD+ synthesis.</p>
              <p className="text-sm text-gray-700 mb-2"><strong>Example Biomarkers:</strong> Cortisol awakening response, melatonin levels, biological age clocks.</p>
              <p className="text-sm text-gray-700"><strong>Clinical Application:</strong> Circadian entrainment, melatonin, chronological interventions, qEEG-driven neurotherapy.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Selected References",
      content: (
        <div className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-3 text-sm">
              <p>1. López-Otín C, et al. Hallmarks of aging: An autophagic perspective. <em>Front Endocrinol</em>. 2018.</p>
              <p>2. Kennedy BK, et al. The multifaceted role of nutrient sensing and mTORC1 signaling... <em>Front Aging</em>. 2021.</p>
              <p>3. Bonkowski MS, Sinclair DA. Slowing ageing by design: the rise of NAD+ and sirtuin-activating compounds. <em>Nat Rev Mol Cell Biol</em>. 2016.</p>
              <p>4. Salminen A, Kaarniranta K. NF-κB in aging and disease. <em>J Mol Med</em>. 2009.</p>
              <p>5. Foresto-Neto O, et al. The Role of the JAK/STAT Signaling Pathway in...Alzheimer's disease. <em>Int J Mol Sci</em>. 2022.</p>
              <p>6. Mannick JB, et al. mTOR inhibition improves immune function in the elderly. <em>Sci Transl Med</em>. 2014.</p>
            </div>
            <div className="space-y-3 text-sm">
              <p>7. Hickson LJ, et al. Senolytics in idiopathic pulmonary fibrosis... <em>EBioMedicine</em>. 2019.</p>
              <p>8. Yoshino M, et al. Nicotinamide mononucleotide increases muscle insulin sensitivity... <em>Science</em>. 2021.</p>
              <p>9. Barzilai N, et al. Metformin as a Tool to Target Aging. <em>Cell Metab</em>. 2016.</p>
              <p>10. Goodell MA, Rando TA. Stem cells and healthy aging. <em>Science</em>. 2015.</p>
              <p>11. Partridge L, et al. Facing up to the global challenges of ageing. <em>Nature</em>. 2018.</p>
            </div>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg mt-6">
            <p className="text-sm text-gray-600 italic text-center">
              *(A more comprehensive list can be provided as a handout)*
            </p>
          </div>
        </div>
      )
    }
  ];

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft" && currentSlide > 0) {
        setCurrentSlide(currentSlide - 1);
      } else if (event.key === "ArrowRight" && currentSlide < slides.length - 1) {
        setCurrentSlide(currentSlide + 1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentSlide, slides.length]);

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h1>
          <p className="text-gray-600">Please sign in to access this lecture.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb Navigation */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-2 py-4 text-sm text-gray-500">
            <Link href="/mentorship" className="hover:text-gray-700 flex items-center">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Mentorship
            </Link>
            <span>/</span>
            <span className="text-gray-900">Cellular Basis of Health and Disease</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <Link href="/mentorship" className="inline-flex items-center text-blue-600 hover:text-blue-800">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Mentorship Program
            </Link>
            <LectureDownloader lectureName="Cellular Basis Health Disease" fileName="CellularBasisHealthDisease" />
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                SHIFT Framework
              </span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Cellular Basis of Health and Disease
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A comprehensive primer on longevity medicine fundamentals for physicians
            </p>
          </div>
        </div>

        {/* Slide Counter */}
        <div className="text-center mb-6">
          <span className="text-sm text-gray-500">
            Slide {currentSlide + 1} of {slides.length}
          </span>
        </div>

        {/* Main Slide Container */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Slide Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
            <h2 className="text-2xl font-bold">{slides[currentSlide].title}</h2>
            {slides[currentSlide].subtitle && (
              <p className="text-blue-100 mt-2">{slides[currentSlide].subtitle}</p>
            )}
          </div>

          {/* Slide Content */}
          <div className="p-8">
            {slides[currentSlide].content}
          </div>

          {/* Navigation */}
          <div className="bg-gray-50 px-6 py-4 flex items-center justify-between">
            <button
              onClick={() => setCurrentSlide(Math.max(0, currentSlide - 1))}
              disabled={currentSlide === 0}
              className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Previous
            </button>

            <div className="flex space-x-2">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-3 h-3 rounded-full ${
                    index === currentSlide ? "bg-blue-600" : "bg-gray-300"
                  }`}
                />
              ))}
            </div>

            <button
              onClick={() => setCurrentSlide(Math.min(slides.length - 1, currentSlide + 1))}
              disabled={currentSlide === slides.length - 1}
              className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span>Next</span>
              <ChevronRight className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>

        {/* Embedded Audio Lecture */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden mt-8">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Accompanying Audio Lecture</h3>
            <p className="text-sm text-gray-600 mt-1">
              Listen to the comprehensive audio guide while navigating through the slides
            </p>
          </div>
          <div className="p-6 flex justify-center">
            <iframe 
              src="https://share.descript.com/embed/CbRym8ejfLi" 
              width="640" 
              height="360" 
              frameBorder="0" 
              allowFullScreen
              className="rounded-lg shadow-sm"
              title="Cellular Basis of Health and Disease Audio Lecture"
            ></iframe>
          </div>
        </div>
      </div>

      {/* Keyboard Navigation */}
      <div className="fixed bottom-4 right-4 bg-white p-2 rounded-lg shadow-md">
        <p className="text-xs text-gray-500 font-light">Use ← → arrows to navigate</p>
      </div>
    </div>
  );
}